import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { MEDIA_LIBRARY } from 'constants/routes';
import cn from 'classnames';
// Api
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
// Types
import { StoreStatus } from 'api/graphql-global-types';
import {
  GetMediaPosts,
  GetMediaPosts_getMediaPosts_entities,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
// Ui
import Loader from 'ui3/Loader/Loader';
// Components
import MediaFeedListItem from '../MediaFeedList/MediaFeedListItem/MediaFeedListItem';
import Section from 'components/common3/Section/Section';
import CarouselSlider from 'components/common3/CarouselSlider/CarouselSlider';
// Styles
import styles from './MediaFeedList.module.scss';

type MediaFeedListProp = {
  storeId: string;
  // Optional: when provided, the query is keyed by `storeSlug` instead of
  // `storeId`. Post pages prefetch by `storeSlug` (available from the URL
  // without waiting on the main post query), so the carousel must pass the
  // slug too — otherwise the SSR-warmed cache entry would not be hit on
  // hydration now that `getMediaPosts.keyArgs` includes both identifiers.
  storeSlug?: string;
  mediaFeedId?: string;
  hideSeeAll?: boolean;
  hasMediaPost?: boolean;
  customTitle?: string;
  className?: string;
  allMediaUrl?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  // Server-prefetched entities. When provided, the carousel renders
  // synchronously on first SSR pass without depending on Apollo's SSR walker
  // — used by `social-media-post/[mediaPostSlug]` to ship the related-posts
  // links in the initial HTML.
  initialEntities?: GetMediaPosts_getMediaPosts_entities[] | null;
};

const MediaFeedList = ({
  storeId,
  storeSlug,
  mediaFeedId,
  customTitle,
  hideSeeAll = false,
  hasMediaPost = true,
  className,
  allMediaUrl,
  fetchPolicy = 'network-only',
  initialEntities,
}: MediaFeedListProp) => {
  const { data: mediaPosts, loading: mediaPostsLoading } = useQuery<
    GetMediaPosts,
    GetMediaPostsVariables
  >(GET_MEDIA_POSTS, {
    variables: {
      input: storeSlug
        ? {
            storeSlug,
            storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
          }
        : {
            storeId,
            storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
          },
    },
    fetchPolicy,
    skip: !hasMediaPost,
  });

  const entities = mediaPosts?.getMediaPosts?.entities ?? initialEntities ?? [];
  const mediaFeeds = entities.filter((post) => post.id !== mediaFeedId);

  if (!mediaFeeds.length) {
    return null;
  }

  const title = customTitle?.length ? customTitle : 'Your media feed';

  return (
    <Section
      title={title}
      className={cn(styles.root, className)}
      seeAllLink={hideSeeAll ? '' : allMediaUrl || MEDIA_LIBRARY}
      seeAllLinkTitle={hideSeeAll ? '' : 'Show All'}
    >
      <Loader loading={mediaPostsLoading} size="large" />

      <div className={styles.rootCarousel}>
        <CarouselSlider>
          {mediaFeeds.map((item, index) => (
            <MediaFeedListItem
              key={item.id}
              mediaPost={item}
              priorityImageLoading={index <= 1}
            />
          ))}
        </CarouselSlider>
      </div>
    </Section>
  );
};

export default MediaFeedList;
